import { useState, useEffect, useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import { useIsMutating } from '@tanstack/react-query';
import { BsEnvelope, BsExclamationTriangle, BsPauseFill, BsPlayFill, BsPlus, BsTrash } from 'react-icons/bs';

import { updateUserProfile } from '#authFeature/api/userService';
import TermsAcceptanceModal from '#emailCampaignFeature/components/TermsAcceptanceModal/TermsAcceptanceModal';
import Alert from '#globalComponents/Alert/Alert';
import Button from '#globalComponents/Button/Button';
import CollectionToolbar from '#globalComponents/CollectionView/CollectionToolbar';
import CollectionView from '#globalComponents/CollectionView/CollectionView';
import useCollectionSearch from '#globalComponents/CollectionView/useCollectionSearch';
import useCollectionViewMode from '#globalComponents/CollectionView/useCollectionViewMode';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import Page from '#globalComponents/Page/Page';
import PageHeader from '#globalComponents/PageHeader/PageHeader';
import { useAuth } from '#globalContexts/AuthProvider';
import { useError } from '#globalContexts/ErrorContext';
import { useAllEmailAccounts } from '#settingsFeature/models/emailAccounts/queries';

import CampaignKPICards from '../../components/common/CampaignKPICards/CampaignKPICards';
import CreateCampaignModal from '../../components/common/CreateCampaignModal/CreateCampaignModal';
import { getCampaignStatusTag } from '../../constants/campaignStatusTags';
import { useEmailCampaignsContext } from '../../contexts/EmailCampaignsContext';
import { emailCampaignsMutationKeys } from '../../models/emailCampaigns/keys';

const TABS = [
	{ key: 'all', label: 'Alle' },
	{ key: 'active', label: 'Aktiv' },
	{ key: 'paused', label: 'Pausiert' },
	{ key: 'completed', label: 'Abgeschlossen' },
];

const tabCountColors = {
	all: { active: 'bg-primary-200 text-primary-700', inactive: 'bg-bgBase-400 text-bgBase-800' },
	active: { active: 'bg-success-200 text-success-700', inactive: 'bg-success-200 text-success-700' },
	paused: { active: 'bg-caution-200 text-caution-700', inactive: 'bg-caution-200 text-caution-700' },
	completed: { active: 'bg-bgBase-500 text-text', inactive: 'bg-bgBase-400 text-bgBase-800' },
};

const getSearchText = (campaign) => campaign.campaignName;

const MailCampaignsOverviewPage = () => {
	const navigate = useNavigate();
	const [viewMode, setViewMode] = useCollectionViewMode('emailCampaigns');
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [showTermsModal, setShowTermsModal] = useState(false);
	const [isAccepting, setIsAccepting] = useState(false);
	const [activeTab, setActiveTab] = useState('all');
	const [showConfirm, setShowConfirm] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [pendingCampaign, setPendingCampaign] = useState(null);
	const [campaignToDelete, setCampaignToDelete] = useState(null);

	const { emailCampaigns, loadingCampaigns, handleUpdateCampaign, handleDeleteCampaign } = useEmailCampaignsContext();

	const updatingCampaign = useIsMutating({ mutationKey: emailCampaignsMutationKeys.update }) > 0;
	const deletingCampaign = useIsMutating({ mutationKey: emailCampaignsMutationKeys.delete }) > 0;

	const { user, termsAcceptedAt, refetchUserProfile } = useAuth();
	const { showError, showWarning } = useError();
	const { data: emailAccounts = [], isLoading: isLoadingAccounts } = useAllEmailAccounts();
	const hasConnectedAccount =
		emailAccounts.length > 0 && emailAccounts.some((acc) => acc.connectionStatus === 'connected');

	useEffect(() => {
		if (user && termsAcceptedAt === null) {
			setShowTermsModal(true);
		}
	}, [user, termsAcceptedAt]);

	const { search, setSearch, filteredItems: searchedCampaigns } = useCollectionSearch(emailCampaigns, getSearchText);
	const filteredCampaigns = useMemo(
		() =>
			activeTab === 'all' ? searchedCampaigns : searchedCampaigns.filter((c) => c.campaignStatus === activeTab),
		[searchedCampaigns, activeTab],
	);

	const tabCounts = useMemo(
		() => ({
			all: emailCampaigns.length,
			active: emailCampaigns.filter((c) => c.campaignStatus === 'active').length,
			paused: emailCampaigns.filter((c) => c.campaignStatus === 'paused').length,
			completed: emailCampaigns.filter((c) => c.campaignStatus === 'completed').length,
		}),
		[emailCampaigns],
	);

	const handleAcceptTerms = async () => {
		if (!user?.id || isAccepting) return;
		setIsAccepting(true);
		try {
			const { error } = await updateUserProfile(user.id, { termsAcceptedAt: new Date().toISOString() });
			if (error) {
				showError({
					title: 'Fehler beim Speichern',
					message: 'Die Nutzungsbedingungen konnten nicht gespeichert werden.',
					autoClose: false,
				});
			} else {
				await refetchUserProfile();
				setShowTermsModal(false);
			}
		} catch {
			showError({
				title: 'Fehler beim Speichern',
				message: 'Die Nutzungsbedingungen konnten nicht gespeichert werden.',
				autoClose: false,
			});
		} finally {
			setIsAccepting(false);
		}
	};

	const handlePlayClick = (campaign) => {
		if (!campaign.emailAccountId) {
			showWarning({
				title: 'E-Mail-Konto erforderlich',
				message: 'Bitte wählen Sie zuerst ein E-Mail-Konto in den Kampagneneinstellungen aus.',
				autoClose: true,
				autoCloseDuration: 5000,
			});
			return;
		}
		setPendingCampaign(campaign);
		setShowConfirm(true);
	};

	const handleConfirmSend = async () => {
		if (!pendingCampaign) return;
		try {
			await handleUpdateCampaign({
				id: pendingCampaign.id,
				updates: { campaignStatus: 'active' },
				prevStatus: pendingCampaign.campaignStatus,
			});
			setShowConfirm(false);
			setPendingCampaign(null);
		} catch {
			/* handled by context */
		}
	};

	const handleCancel = () => {
		setShowConfirm(false);
		setPendingCampaign(null);
	};

	const handlePauseClick = async (campaign) => {
		try {
			await handleUpdateCampaign({
				id: campaign.id,
				updates: { campaignStatus: 'paused' },
				prevStatus: campaign.campaignStatus,
			});
		} catch {
			/* handled by context */
		}
	};

	const handleDeleteClick = (campaign) => {
		setCampaignToDelete(campaign);
		setShowDeleteModal(true);
	};

	const handleConfirmDelete = async () => {
		if (!campaignToDelete) return;
		try {
			await handleDeleteCampaign(campaignToDelete.id);
			setShowDeleteModal(false);
			setCampaignToDelete(null);
		} catch {
			/* handled by context */
		}
	};

	const handleCancelDelete = () => {
		setShowDeleteModal(false);
		setCampaignToDelete(null);
	};

	const isBusy = updatingCampaign || deletingCampaign;
	const items = filteredCampaigns.map((campaign) => {
		const mailCount = campaign.emailCampaignSteps?.length || 0;
		const listCount = campaign.emailCampaign_emailList?.length || 0;
		const isActive = campaign.campaignStatus === 'active';
		const canStart = !isActive && campaign.campaignStatus !== 'completed';
		return {
			id: campaign.id,
			title: campaign.campaignName,
			onOpen: () => navigate(`/email-campaigns/${campaign.id}`),
			onRename: (campaignName) => handleUpdateCampaign({ id: campaign.id, updates: { campaignName } }),
			stats: [
				{ value: mailCount, label: mailCount === 1 ? 'E-Mail' : 'E-Mails' },
				{ value: listCount, label: listCount === 1 ? 'Liste' : 'Listen' },
			],
			tags: [getCampaignStatusTag(campaign.campaignStatus)],
			date: { label: 'Erstellt am', value: campaign.createdAt },
			actions: [
				isActive && {
					key: 'pause',
					icon: BsPauseFill,
					label: 'Pausieren',
					disabled: isBusy,
					onClick: () => handlePauseClick(campaign),
				},
				canStart && {
					key: 'start',
					icon: BsPlayFill,
					label: 'Starten',
					disabled: isBusy,
					onClick: () => handlePlayClick(campaign),
				},
				{
					key: 'delete',
					icon: BsTrash,
					label: 'Löschen',
					danger: true,
					disabled: isBusy,
					onClick: () => handleDeleteClick(campaign),
				},
			].filter(Boolean),
		};
	});

	return (
		<Page header={<PageHeader breadcrumbs={['E-Mail Kampagnen']} />}>
			<div className="mb-6">
				<CampaignKPICards />
			</div>

			<CollectionToolbar
				search={search}
				onSearchChange={setSearch}
				searchPlaceholder="Kampagne suchen ..."
				viewMode={viewMode}
				onViewModeChange={setViewMode}
			>
				<Button shape="pill" size="sm" onClick={() => setShowCreateModal(true)}>
					<BsPlus className="h-3.5 w-3.5" />
					Neue Kampagne
				</Button>
			</CollectionToolbar>

			<div className="mb-5">
				<div className="inline-flex items-center rounded-card bg-bgBase-300 border border-bgBase-500 p-1 gap-1">
					{TABS.map((tab) => {
						const isActive = activeTab === tab.key;
						const colors = tabCountColors[tab.key];
						return (
							<button
								key={tab.key}
								onClick={() => setActiveTab(tab.key)}
								className={`inline-flex items-center gap-1.5 rounded-control px-3 py-1.5 text-sm font-medium transition-all ${
									isActive ? 'bg-white text-text' : 'text-bgBase-800 hover:text-text'
								}`}
							>
								{tab.label}
								<span
									className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${isActive ? colors.active : colors.inactive}`}
								>
									{tabCounts[tab.key]}
								</span>
							</button>
						);
					})}
				</div>
			</div>

			<CollectionView
				items={items}
				viewMode={viewMode}
				isLoading={loadingCampaigns}
				emptyLabel={
					search || activeTab !== 'all' ? 'Keine Kampagnen entsprechen den Filterkriterien.' : undefined
				}
				createLabel="neue Kampagne erstellen"
				onCreate={() => setShowCreateModal(true)}
			/>

			<Modal isOpen={showConfirm} onClose={handleCancel} size="m">
				<Panel.Header
					icon={BsExclamationTriangle}
					onClose={handleCancel}
					subtitle="Die E-Mails werden an alle ausgewählten Leads versendet."
				>
					Kampagne aktivieren
				</Panel.Header>

				<Panel.Body>
					{pendingCampaign && (
						<div className="bg-bgBase-200 rounded-card p-4 mb-5 flex items-center gap-3">
							<div className="w-8 h-8 bg-primary-100 rounded-card grid place-items-center">
								<BsEnvelope className="w-4 h-4 text-primary-500" />
							</div>
							<div>
								<p className="text-sm font-medium text-text">{pendingCampaign.campaignName}</p>
								<p className="text-xs text-bgBase-800">
									{pendingCampaign.emailCampaignSteps?.length || 0} E-Mails &middot;{' '}
									{pendingCampaign.emailCampaign_emailList?.length || 0} Listen
								</p>
							</div>
						</div>
					)}

					{!isLoadingAccounts && !hasConnectedAccount && (
						<Alert
							tone="caution"
							title="E-Mail-Konto erforderlich"
							actionLabel="Zu den Einstellungen"
							onAction={() => navigate('/settings?tab=smtp')}
						>
							Um Kampagnen zu versenden, füge zunächst ein E-Mail-Konto hinzu.
						</Alert>
					)}
				</Panel.Body>

				<Panel.Footer className="justify-end">
					<Button onClick={handleCancel} variant="outline" size="sm">
						Abbrechen
					</Button>
					<Button
						onClick={handleConfirmSend}
						disabled={!hasConnectedAccount || isLoadingAccounts}
						variant="solid"
						size="sm"
						loading={isLoadingAccounts}
					>
						{!isLoadingAccounts && <BsEnvelope className="w-4 h-4 mr-1" />}
						{isLoadingAccounts ? 'Prüfe...' : 'Kampagne aktivieren'}
					</Button>
				</Panel.Footer>
			</Modal>

			<ConfirmModal
				isOpen={showDeleteModal}
				onClose={handleCancelDelete}
				onConfirm={handleConfirmDelete}
				title="E-Mail-Kampagne löschen"
				message={`Möchten Sie die Kampagne "${campaignToDelete?.campaignName}" wirklich löschen?`}
				confirmText="Kampagne löschen"
				cancelText="Abbrechen"
				isLoading={deletingCampaign}
			/>

			<CreateCampaignModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} />

			<TermsAcceptanceModal
				isOpen={showTermsModal}
				onClose={() => {
					setShowTermsModal(false);
					navigate('/');
				}}
				onAccept={handleAcceptTerms}
			/>
		</Page>
	);
};

export default MailCampaignsOverviewPage;
